import { useContext, useState, useEffect } from "react";
import moment from "moment-timezone";

import { useNavigate, useParams } from "react-router-dom";

import useLocation from "../hooks/useLocation";
import useLocations from "../hooks/useLocations";
import useEvent from "../hooks/useEvent";
import useTags from "../hooks/useTags";
import useHomePage from "../hooks/useHomePage";
import { useAuth } from "../hooks/useAuth";

import { ToastContext } from "../Components/ToastProvider";
import TimePicker from "../Components/TimePicker";
import LoadingButton from "../Components/LoadingButton";
// import Select from "@mui/material/Select";
// import FormControl from "@mui/material/FormControl";
// import MenuItem from "@mui/material/MenuItem";

import CompleteProfileModal from "./CompleteProfileModal";

import "../App.css";

function CheckboxChip({ id, value, label, checked, onChange }) {
  return (
    <label className={`chip ${checked ? "chip--selected" : ""}`}>
      <input
        type="checkbox"
        id={id}
        value={value}
        checked={checked}
        onChange={onChange}
        style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
      />
      {label}
    </label>
  );
}

function CreateEvent() {
  const { id } = useParams();
  const { createToast } = useContext(ToastContext);
  const { location, setLocation, error } = useLocation(id);
  const { profile, setCity } = useAuth();
  const { createEvent, errorEvent } = useEvent(null, true);
  const { cityId } = useAuth();
  const { tags } = useTags();

  const twoDays = moment().add(2, "days");
  const twoDaysFormatted = twoDays.format("YYYY-MM-DD");
  const twoMonths = moment().add(2, "months");
  const twoMonthsFormatted = twoMonths.format("YYYY-MM-DD");

  const [disabled, setDisabled] = useState(false);
  const navigate = useNavigate();
  const { cities, cityTitle } = useHomePage();

  const [tagsSelected, setTagsSelected] = useState([]);
  const [description, setDescription] = useState("");
  const [chosenDish, setChosenDish] = useState(null);
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [date, setDate] = useState(twoDaysFormatted);
  const [time, setTime] = useState(moment("12:30", "HH:mm"));
  const [title, setTitle] = useState("");
  const [languages, setLanguages] = useState("English");
  const [spotSearch, setSpotSearch] = useState("");

  const [locationFree, setLocationFree] = useState("");
  const [addressFree, setAddressFree] = useState("");
  const [mapUrl, setMapUrl] = useState("");
  const [dishFree, setDishFree] = useState("");

  const { locations } = useLocations();
  const [isCompleteProfileModalOpen, toggleIsCompleteProfileModalOpen] =
    useState(false);

  const city = cities.find((c) => c.id === cityId);

  const setToLocationTimezone = (dateTime, timezone) => {
    return moment(dateTime).tz(timezone, true);
  };

  useEffect(() => {
    setLocation(null);
  }, [city, setLocation]);

  const filteredLocations =
    locations?.filter((loc) =>
      loc.title.toLowerCase().includes(spotSearch.toLowerCase()),
    ) ?? [];

  const handleLocationClick = (loc) => {
    setLocation(loc);
    setSpotSearch("");
    if (chosenDish) {
      const stillValid = loc.dishes?.find((d) => d.id === chosenDish.id);
      if (!stillValid) setChosenDish(null);
    }
  };

  // const handleCityChange = (e) => {
  //   setCity(e.target.value);
  // };

  const handleClearSpot = () => {
    setLocation(null);
    setChosenDish(null);
    setSpotSearch("");
  };

  const switchToCustomMode = () => {
    setIsCustomMode(true);
    setLocation(null);
    setChosenDish(null);
    setSpotSearch("");
  };

  const switchToCuratedMode = () => {
    setIsCustomMode(false);
    setLocationFree("");
    setAddressFree("");
    setMapUrl("");
    setDishFree("");
  };

  const submitEvent = ({ commandFromCompleteProfileModal }) => {
    if (!location && !locationFree) {
      return;
    }
    if (
      (!profile.favorite_food ||
        !profile.gender ||
        !profile.spice_level ||
        !profile.ask_me_about) &&
      !commandFromCompleteProfileModal
    ) {
      toggleIsCompleteProfileModalOpen(true);
      return;
    }

    setDisabled(true);

    const dateTime = `${date}T${moment(time).format("HH:mm:ss")}`;
    const dateLocationTimezone = setToLocationTimezone(
      dateTime,
      city?.timezone,
    );
    const utcDateStr = moment(dateLocationTimezone).utc().toISOString();

    let data = {
      title,
      body: description,
      date: utcDateStr,
      dish_id: chosenDish?.id,
      tag_ids: tagsSelected,
      languages,
      custom_location: isCustomMode ? locationFree : "",
      custom_address: isCustomMode ? addressFree : "",
      custom_google_maps: isCustomMode ? mapUrl : "",
      custom_dish: isCustomMode ? dishFree : "",
      timezone: city.timezone,
      city_id: cityId,
    };

    if (!isCustomMode && location) {
      data.location_id = location.id;
    }

    createEvent(data).then(async (id) => {
      createToast("You created an event! 🎆", "success", true);
      navigate(`../../events/${id}`, { replace: true });
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submitEvent({});
  };

  function range(start, end) {
    const result = [];
    for (let i = start; i < end; i++) result.push(i);
    return result;
  }

  const disabledTime = () => ({
    disabledHours: () => range(0, 8),
  });

  const handleTagToggle = (id) => {
    setTagsSelected((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );
  };

  return (
    <div className="form-page">
      <div className="form-page-header">
        <h1>Create your event</h1>
        <p>Pick something delicious. Invite the table.</p>
      </div>

      {error && <p className="error-message">Something went wrong...</p>}
      {errorEvent && <p className="error-message">Something went wrong...</p>}

      <form onSubmit={handleSubmit}>
        <section className="profile-card">
          {/* Event name */}
          <div className="field">
            <label className="field-label" htmlFor="title">
              Event name <span className="required-field">*</span>
            </label>
            <input
              id="title"
              type="text"
              required
              placeholder="My Awesome Event"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="field-divider" />

          {/* Location block */}
          <div className="field">
            <label>City</label>
            <p className="field-hint">
              You can change it in the city dropdown in the top navigation
            </p>
            <input type="text" value={cityTitle} disabled />
          </div>
          <div className="field">
            <label className="field-label">
              Location <span className="required-field">*</span>
            </label>
            {/* {cities.length && (
              <FormControl sx={{ m: 1, minWidth: 120 }}>
                <Select
                  value={cityId}
                  onChange={handleCityChange}
                  displayEmpty
                  inputProps={{ "aria-label": "Without label" }}
                >
                  {cities.map((city) => (
                    <MenuItem value={city.id}>{city.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )} */}
            <div className="location-block">
              <div className="location-block-header">
                <span className="location-block-title">
                  {isCustomMode
                    ? "Custom location"
                    : "Choose from our curated spots"}
                </span>
                <button
                  type="button"
                  className="link-btn"
                  onClick={
                    isCustomMode ? switchToCuratedMode : switchToCustomMode
                  }
                >
                  {isCustomMode
                    ? "Choose from our curated spots instead"
                    : "Enter a custom location instead"}
                </button>
              </div>

              {/* Curated mode */}
              {!isCustomMode && (
                <>
                  {!location ? (
                    <>
                      <input
                        type="text"
                        className="spot-search"
                        placeholder="Search spots..."
                        value={spotSearch}
                        onChange={(e) => setSpotSearch(e.target.value)}
                      />
                      <div className="pills-grid">
                        {filteredLocations.map((loc) => (
                          <button
                            key={loc.id}
                            type="button"
                            className="pill"
                            onClick={() => handleLocationClick(loc)}
                          >
                            {loc.title}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="selected-spot">
                        <span className="selected-spot-name">
                          {location.title}
                        </span>
                        <button
                          type="button"
                          className="link-btn"
                          onClick={handleClearSpot}
                        >
                          Change spot
                        </button>
                      </div>

                      {location.dishes?.length > 0 && (
                        <div className="dish-picker">
                          <span className="dish-picker-label">
                            What do you want to try?
                          </span>
                          <p className="field-hint">
                            ✨ Pick one dish or drink to anchor the meetup.
                            Everyone orders what they want, this is just the
                            thing you're excited about.
                          </p>
                          <div className="dish-options">
                            {location.dishes.map((dish) => (
                              <label
                                key={dish.id}
                                className={`dish-option${chosenDish?.id === dish.id ? " selected" : ""}`}
                              >
                                <input
                                  type="radio"
                                  name="dish"
                                  value={dish.id}
                                  checked={chosenDish?.id === dish.id}
                                  onChange={() => setChosenDish(dish)}
                                />
                                {dish.image_url && (
                                  <img
                                    src={dish.image_url}
                                    alt={dish.title}
                                    className="dish-option-img"
                                  />
                                )}
                                <div className="dish-option-text">
                                  <span className="dish-option-name">
                                    {dish.title}
                                  </span>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Custom mode */}
              {isCustomMode && (
                <div className="custom-location-fields">
                  <div className="custom-location-row">
                    <div>
                      <label>
                        Restaurant or venue name{" "}
                        <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        value={locationFree}
                        required
                        onChange={(e) => setLocationFree(e.target.value)}
                      />
                    </div>
                    <div>
                      <label>
                        Address <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={addressFree}
                        onChange={(e) => setAddressFree(e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <label>Google Maps Link</label>
                    <input
                      type="url"
                      value={mapUrl}
                      onChange={(e) => setMapUrl(e.target.value)}
                    />
                  </div>
                  <div className="dish-picker">
                    <label htmlFor="dishFree">
                      What do you want to try?{" "}
                      <span className="required-field">*</span>
                    </label>
                    <input
                      id="dishFree"
                      type="text"
                      required
                      placeholder="The masala dosa, the coconut coffee, the mango sticky rice..."
                      value={dishFree}
                      onChange={(e) => setDishFree(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="field-divider" />

          {/* Description */}
          <div className="field">
            <label className="field-label" htmlFor="description">
              About this meetup <span className="required-field">*</span>
            </label>
            <p className="field-hint">
              Tell people what you're excited about. A sentence or two is
              plenty.
            </p>
            <textarea
              id="description"
              value={description}
              required
              rows="5"
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Languages */}
          <div className="field">
            <label className="field-label" htmlFor="languages">
              Languages you speak <span className="required-field">*</span>
            </label>
            <p className="field-hint">
              Helps guests know who they'll be able to chat with.
            </p>
            <input
              id="languages"
              className="capitalize"
              type="text"
              required
              value={languages}
              onChange={(e) => setLanguages(e.target.value)}
            />
          </div>

          <div className="field-divider" />

          {/* Tags */}
          <div className="field">
            <label className="field-label">Meetup Vibes</label>
            <p className="field-hint">
              Choose any tags that describe your meetup.
            </p>
            <div className="chips">
              {tags?.map((tag) => (
                <CheckboxChip
                  key={tag.id}
                  id={`tag-${tag.id}`}
                  value={tag.id}
                  label={tag.title}
                  checked={tagsSelected.includes(tag.id)}
                  onChange={() => handleTagToggle(tag.id)}
                />
              ))}
            </div>
          </div>

          <div className="field-divider" />

          {/* Date */}
          <div className="field">
            <label className="field-label" htmlFor="date">
              Date <span className="required-field">*</span>
            </label>
            <input
              id="date"
              value={date}
              type="date"
              name="date"
              aria-label="date"
              required
              min={twoDaysFormatted}
              max={twoMonthsFormatted}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          {/* Time */}
          <div className="field">
            <label className="field-label" htmlFor="time">
              Time <span className="required-field">*</span>
            </label>
            <TimePicker
              id="time"
              required
              aria-label="time"
              minuteStep={15}
              format="HH:mm"
              showNow={false}
              disabledTime={disabledTime}
              value={time}
              onChange={(t) => setTime(t)}
            />
            <p className="field-hint">Timezone: {city?.timezone}</p>
          </div>
        </section>

        <div className="save-row">
          <LoadingButton
            type="submit"
            disabled={disabled}
            color="primary"
            variant="contained"
          >
            Create your event
          </LoadingButton>
        </div>
      </form>

      <CompleteProfileModal
        isOpen={isCompleteProfileModalOpen}
        toggleOpen={toggleIsCompleteProfileModalOpen}
        onSave={submitEvent}
      />
    </div>
  );
}

export default CreateEvent;
